/**
 * Overlay Template Engine
 * Renders overlay templates to transparent PNGs using HTML Canvas.
 */

import type { OverlayBuilderConfig, OverlayTemplateId } from '@/types';

// ============================================================================
// Constants
// ============================================================================

const CANVAS_WIDTH_4_3 = 2400;
const CANVAS_HEIGHT_4_3 = 1800;
const CANVAS_WIDTH_1_1 = 2400;
const CANVAS_HEIGHT_1_1 = 2400;

// ============================================================================
// Template Definitions
// ============================================================================

export interface OverlayTemplate {
  id: OverlayTemplateId;
  label: string;
  description: string;
}

export const OVERLAY_TEMPLATES: OverlayTemplate[] = [
  { id: 'bar', label: 'Leiste', description: 'Untere Leiste mit Logo & Text' },
  { id: 'corner', label: 'Ecke', description: 'L-förmiger Akzent in der Ecke' },
  { id: 'frame', label: 'Rahmen', description: 'Rahmen mit unterem Balken' },
];

// ============================================================================
// Google Fonts List (popular fonts, sorted alphabetically)
// ============================================================================

export const GOOGLE_FONTS_LIST: string[] = [
  'Abel', 'Abril Fatface', 'Alegreya', 'Alegreya Sans', 'Amatic SC',
  'Anton', 'Archivo', 'Archivo Narrow', 'Arimo', 'Assistant',
  'Barlow', 'Barlow Condensed', 'Be Vietnam Pro', 'Bitter', 'Cabin',
  'Cairo', 'Catamaran', 'Comfortaa', 'Cormorant Garamond', 'Crimson Text',
  'DM Sans', 'DM Serif Display', 'Dancing Script', 'Dosis', 'EB Garamond',
  'Exo 2', 'Figtree', 'Fira Sans', 'Fjalla One', 'Francois One',
  'Geologica', 'Great Vibes', 'Heebo', 'Hind', 'IBM Plex Sans',
  'Inconsolata', 'Inter', 'Josefin Sans', 'Jost', 'Kanit',
  'Karla', 'Lato', 'Lexend', 'Libre Baskerville', 'Libre Franklin',
  'Lilita One', 'Lora', 'Manrope', 'Maven Pro', 'Merriweather',
  'Montserrat', 'Mukta', 'Mulish', 'Nunito', 'Nunito Sans',
  'Old Standard TT', 'Open Sans', 'Oswald', 'Outfit', 'Overpass',
  'PT Sans', 'PT Serif', 'Pacifico', 'Permanent Marker', 'Playfair Display',
  'Plus Jakarta Sans', 'Poppins', 'Prompt', 'Quicksand', 'Raleway',
  'Red Hat Display', 'Roboto', 'Roboto Condensed', 'Roboto Slab', 'Rubik',
  'Satisfy', 'Signika', 'Slabo 27px', 'Source Code Pro', 'Source Sans 3',
  'Space Grotesk', 'Spectral', 'Titillium Web', 'Ubuntu', 'Urbanist',
  'Varela Round', 'Work Sans', 'Yanone Kaffeesatz', 'Noto Sans', 'Noto Serif',
];

// ============================================================================
// Dynamic Font Loading
// ============================================================================

const loadedFonts = new Set<string>();

export const loadGoogleFont = async (fontFamily: string): Promise<void> => {
  if (loadedFonts.has(fontFamily)) return;

  // Inject CSS link for the font
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;600;700&display=swap`;
  document.head.appendChild(link);

  // Wait for font to be ready for canvas rendering
  try {
    await document.fonts.load(`600 48px "${fontFamily}"`);
    await document.fonts.ready;
    loadedFonts.add(fontFamily);
  } catch {
    // Mark as loaded anyway to avoid retrying
    loadedFonts.add(fontFamily);
  }
};

// ============================================================================
// Color Utilities
// ============================================================================

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getContrastColor = (hex: string): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// ============================================================================
// Logo Loading
// ============================================================================

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

// ============================================================================
// Template Renderers
// ============================================================================

const renderBarTemplate = (
  ctx: CanvasRenderingContext2D,
  config: OverlayBuilderConfig,
  w: number,
  h: number,
  logoImg: HTMLImageElement | null
): void => {
  const barHeightFrac = config.barHeight ?? 0.10;
  const barOpacity = config.barOpacity ?? 0.85;
  const textScale = config.textScale ?? 1.0;

  const barHeight = Math.round(h * barHeightFrac);
  const barY = h - barHeight;
  const padding = Math.round(w * 0.02);

  // Bar background
  ctx.fillStyle = hexToRgba(config.accentColor, barOpacity);
  ctx.fillRect(0, barY, w, barHeight);

  // Thin accent line on top of bar
  ctx.fillStyle = config.accentColor;
  ctx.fillRect(0, barY, w, Math.round(h * 0.004));

  const textColor = getContrastColor(config.accentColor);
  const isLogoLeft = config.logoPosition === 'bottom-left' || config.logoPosition === 'top-left';

  // Logo
  if (logoImg) {
    const logoH = Math.round(barHeight * 0.85);
    const logoW = Math.round(logoH * (logoImg.width / logoImg.height) * (config.logoScale / 0.2));
    const maxLogoW = Math.round(w * config.logoScale);
    const finalLogoW = Math.min(logoW, maxLogoW);
    const finalLogoH = Math.round(finalLogoW * (logoImg.height / logoImg.width));
    const logoY = barY + Math.round((barHeight - finalLogoH) / 2);
    const logoX = isLogoLeft ? padding : w - padding - finalLogoW;

    ctx.drawImage(logoImg, logoX, logoY, finalLogoW, finalLogoH);
  }

  // Text
  if (config.text) {
    const fontSize = Math.round(barHeight * 0.30 * textScale);
    ctx.font = `600 ${fontSize}px '${config.fontFamily}', sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'middle';
    const textY = barY + Math.round(barHeight / 2);

    if (isLogoLeft) {
      ctx.textAlign = 'right';
      ctx.fillText(config.text, w - padding, textY);
    } else {
      ctx.textAlign = 'left';
      ctx.fillText(config.text, padding, textY);
    }
  }
};

const renderCornerTemplate = (
  ctx: CanvasRenderingContext2D,
  config: OverlayBuilderConfig,
  w: number,
  h: number,
  logoImg: HTMLImageElement | null
): void => {
  const textScale = config.textScale ?? 1.0;
  const stripWidth = Math.round(w * 0.025);
  const barHeight = Math.round(h * 0.08);
  const padding = Math.round(w * 0.02);

  // Horizontal bottom strip
  ctx.fillStyle = hexToRgba(config.accentColor, 0.85);
  ctx.fillRect(0, h - barHeight, w, barHeight);

  // Vertical left strip
  ctx.fillStyle = hexToRgba(config.accentColor, 0.70);
  ctx.fillRect(0, h - barHeight - Math.round(h * 0.30), stripWidth, Math.round(h * 0.30));

  // Corner accent square
  ctx.fillStyle = config.accentColor;
  ctx.fillRect(0, h - barHeight - stripWidth, stripWidth + Math.round(w * 0.01), stripWidth);

  const textColor = getContrastColor(config.accentColor);

  // Logo (in the corner area, above the bar)
  if (logoImg) {
    const logoMaxW = Math.round(w * config.logoScale);
    const logoMaxH = Math.round(barHeight * 0.65);
    const logoRatio = logoImg.width / logoImg.height;
    let logoW = logoMaxW;
    let logoH = Math.round(logoW / logoRatio);
    if (logoH > logoMaxH) {
      logoH = logoMaxH;
      logoW = Math.round(logoH * logoRatio);
    }
    const logoX = padding + stripWidth;
    const logoY = h - barHeight + Math.round((barHeight - logoH) / 2);

    ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
  }

  // Text
  if (config.text) {
    const fontSize = Math.round(barHeight * 0.30 * textScale);
    ctx.font = `600 ${fontSize}px '${config.fontFamily}', sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const textY = h - barHeight + Math.round(barHeight / 2);
    ctx.fillText(config.text, w - padding, textY);
  }
};

const renderFrameTemplate = (
  ctx: CanvasRenderingContext2D,
  config: OverlayBuilderConfig,
  w: number,
  h: number,
  logoImg: HTMLImageElement | null
): void => {
  const textScale = config.textScale ?? 1.0;
  const borderWidth = Math.round(w * 0.015);
  const bottomBarHeight = Math.round(h * 0.08);
  const padding = Math.round(w * 0.02);

  // Top border
  ctx.fillStyle = config.accentColor;
  ctx.fillRect(0, 0, w, borderWidth);

  // Left border
  ctx.fillRect(0, 0, borderWidth, h);

  // Right border
  ctx.fillRect(w - borderWidth, 0, borderWidth, h);

  // Bottom bar — fully opaque like the frame
  ctx.fillStyle = config.accentColor;
  ctx.fillRect(0, h - bottomBarHeight, w, bottomBarHeight);

  // Thin line separating frame from bottom bar
  ctx.fillRect(borderWidth, h - bottomBarHeight - borderWidth, w - borderWidth * 2, borderWidth);

  const textColor = getContrastColor(config.accentColor);
  const isLogoLeft = config.logoPosition === 'bottom-left' || config.logoPosition === 'top-left';

  // Logo
  if (logoImg) {
    const logoMaxW = Math.round(w * config.logoScale);
    const logoMaxH = Math.round(bottomBarHeight * 0.85);
    const logoRatio = logoImg.width / logoImg.height;
    let logoW = logoMaxW;
    let logoH = Math.round(logoW / logoRatio);
    if (logoH > logoMaxH) {
      logoH = logoMaxH;
      logoW = Math.round(logoH * logoRatio);
    }
    const logoY = h - bottomBarHeight + Math.round((bottomBarHeight - logoH) / 2);
    const logoX = isLogoLeft ? padding + borderWidth : w - padding - borderWidth - logoW;

    ctx.drawImage(logoImg, logoX, logoY, logoW, logoH);
  }

  // Text
  if (config.text) {
    const fontSize = Math.round(bottomBarHeight * 0.28 * textScale);
    ctx.font = `600 ${fontSize}px '${config.fontFamily}', sans-serif`;
    ctx.fillStyle = textColor;
    ctx.textBaseline = 'middle';
    const textY = h - bottomBarHeight + Math.round(bottomBarHeight / 2);

    if (isLogoLeft) {
      ctx.textAlign = 'right';
      ctx.fillText(config.text, w - padding - borderWidth, textY);
    } else {
      ctx.textAlign = 'left';
      ctx.fillText(config.text, padding + borderWidth, textY);
    }
  }
};

// ============================================================================
// Main Render Function
// ============================================================================

/**
 * Renders an overlay template to a transparent PNG canvas.
 * Can render to a provided canvas (for live preview) or create a new one (for export).
 */
export const renderOverlayToCanvas = async (
  config: OverlayBuilderConfig,
  targetCanvas?: HTMLCanvasElement
): Promise<HTMLCanvasElement> => {
  // Ensure font is loaded before canvas rendering
  await loadGoogleFont(config.fontFamily);

  const w = config.aspectRatio === '1:1' ? CANVAS_WIDTH_1_1 : CANVAS_WIDTH_4_3;
  const h = config.aspectRatio === '1:1' ? CANVAS_HEIGHT_1_1 : CANVAS_HEIGHT_4_3;

  const canvas = targetCanvas || document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');

  // Clear to transparent
  ctx.clearRect(0, 0, w, h);

  // Load logo if provided
  let logoImg: HTMLImageElement | null = null;
  if (config.logoUrl) {
    try {
      logoImg = await loadImage(config.logoUrl);
    } catch {
      console.warn('[OverlayBuilder] Failed to load logo, rendering without it');
    }
  }

  // Render selected template
  switch (config.templateId) {
    case 'bar':
      renderBarTemplate(ctx, config, w, h, logoImg);
      break;
    case 'corner':
      renderCornerTemplate(ctx, config, w, h, logoImg);
      break;
    case 'frame':
      renderFrameTemplate(ctx, config, w, h, logoImg);
      break;
  }

  return canvas;
};

/**
 * Renders an overlay template and returns it as a PNG Blob.
 */
export const renderOverlayTemplate = async (
  config: OverlayBuilderConfig
): Promise<Blob> => {
  const canvas = await renderOverlayToCanvas(config);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to generate overlay blob'));
    }, 'image/png');
  });
};
